import type { LucideIcon } from 'lucide-react';
import {
    BarChart3,
    BellRing,
    BookText,
    Bot,
    Building2,
    Cpu,
    CreditCard,
    FileBarChart,
    FileText,
    Fingerprint,
    Headphones,
    Handshake,
    Network,
    KeyRound,
    LayoutDashboard,
    LifeBuoy,
    Megaphone,
    MessageSquare,
    Plug,
    ScrollText,
    Settings2,
    ShieldAlert,
    ToggleRight,
    UserMinus,
    Users,
    Webhook,
    Workflow,
} from 'lucide-react';
import type { PlatformAbility, PlatformAction, PlatformSubject } from '@/lib/abilities';

export interface PlatformSidebarItem {
    to: string;
    i18nKey: string;
    icon: LucideIcon;
    action: 'read' | 'manage';
    subject: PlatformSubject;
    exact?: boolean;
    /**
     * RBAC-04: visible to every signed-in operator regardless of role. `read:all`
     * is NOT that predicate — CASL only matches subject 'all' against rules
     * declared on 'all', so 5 of 8 roles failed it.
     */
    everyOperator?: boolean;
    children?: Array<Pick<PlatformSidebarItem, 'to' | 'i18nKey' | 'action' | 'subject' | 'exact'>>;
    /**
     * Extra abilities that also make the parent visible (children stay
     * individually gated), e.g. SUPPORT reaches Partner support via Ticket.
     */
    alsoVisibleWith?: Array<{ action: 'read' | 'manage'; subject: PlatformSubject }>;
}

type NavGateItem = { action: PlatformAction; subject: PlatformSubject; everyOperator?: boolean };

/** Whether the item's own route may be opened (its own ability, or everyOperator). */
export function canOpenNavItem(ability: PlatformAbility, item: NavGateItem): boolean {
    if (item.everyOperator) return ability.rules.length > 0;
    return ability.can(item.action, item.subject);
}

/**
 * Nav gate shared by the sidebar, phone tabs and phone More sheet.
 * `everyOperator` items pass for any ability built from a role (every role has
 * at least one rule); an empty ability (no role) still sees nothing. A parent
 * is also shown for any of its `alsoVisibleWith` abilities.
 */
export function isNavItemVisible(
    ability: PlatformAbility,
    item: NavGateItem & { alsoVisibleWith?: PlatformSidebarItem['alsoVisibleWith'] },
): boolean {
    return canOpenNavItem(ability, item)
        || (item.alsoVisibleWith ?? []).some((alt) => ability.can(alt.action, alt.subject));
}

/** The parent's own route when it may be opened, otherwise its first permitted child. */
export function navItemHref(ability: PlatformAbility, item: PlatformSidebarItem): string {
    if (canOpenNavItem(ability, item)) return item.to;
    return item.children?.find((child) => ability.can(child.action, child.subject))?.to ?? item.to;
}

export interface PlatformSidebarGroup {
    label: string;
    i18nKey: string;
    items: PlatformSidebarItem[];
}

export const platformSidebarGroups: PlatformSidebarGroup[] = [
    {
        label: 'OVERVIEW',
        i18nKey: 'nav.groups.overview',
        items: [
            { to: '/dashboard', i18nKey: 'nav.dashboard', icon: LayoutDashboard, action: 'read', subject: 'all', exact: true, everyOperator: true },
        ],
    },
    {
        label: 'TENANT MANAGEMENT',
        i18nKey: 'nav.groups.tenantManagement',
        items: [
            { to: '/merchants', i18nKey: 'nav.merchants', icon: Building2, action: 'read', subject: 'Merchant' },
            { to: '/settings/users', i18nKey: 'nav.settingsUsers', icon: Users, action: 'manage', subject: 'PlatformUser' },
            { to: '/settings/sessions', i18nKey: 'nav.settingsSessions', icon: KeyRound, action: 'read', subject: 'Session' },
            // Self-service MFA — visible to every operator (RBAC-04: everyOperator, not read:all).
            { to: '/settings/security', i18nKey: 'nav.settingsSecurity', icon: Fingerprint, action: 'read', subject: 'all', everyOperator: true },
        ],
    },
    {
        label: 'SUBSCRIPTIONS & BILLING',
        i18nKey: 'nav.groups.billing',
        items: [
            { to: '/dashboard/billing', i18nKey: 'nav.billingDashboard', icon: BarChart3, action: 'read', subject: 'Billing' },
            { to: '/settings/billing/plans', i18nKey: 'nav.billingPlans', icon: CreditCard, action: 'read', subject: 'Plan' },
        ],
    },
    {
        label: 'SUPPORT OPERATIONS',
        i18nKey: 'nav.groups.support',
        items: [
            { to: '/dashboard/tickets', i18nKey: 'nav.tickets', icon: LifeBuoy, action: 'read', subject: 'Ticket' },
            { to: '/crm', i18nKey: 'nav.crm', icon: Handshake, action: 'read', subject: 'Crm' },
            { to: '/chat', i18nKey: 'nav.chat', icon: MessageSquare, action: 'read', subject: 'Chat' },
            { to: '/dashboard/analytics/support', i18nKey: 'nav.analyticsSupport', icon: Headphones, action: 'read', subject: 'Ticket' },
            { to: '/dashboard/analytics/broadcasts', i18nKey: 'nav.analyticsBroadcasts', icon: Megaphone, action: 'read', subject: 'Broadcast' },
        ],
    },
    {
        label: 'AFFILIATES',
        i18nKey: 'nav.groups.affiliates',
        items: [
            {
                to: '/affiliates',
                i18nKey: 'nav.affiliates',
                icon: Handshake,
                action: 'read',
                subject: 'Affiliate',
                alsoVisibleWith: [{ action: 'read', subject: 'Ticket' }],
                children: [
                    { to: '/affiliates', i18nKey: 'nav.affiliatesPartners', action: 'read', subject: 'Affiliate', exact: true },
                    { to: '/affiliates/referrals', i18nKey: 'nav.affiliatesReferrals', action: 'read', subject: 'Affiliate', exact: true },
                    { to: '/affiliates/commissions', i18nKey: 'nav.affiliatesCommissions', action: 'read', subject: 'Affiliate', exact: true },
                    { to: '/affiliates/payouts', i18nKey: 'nav.affiliatesPayouts', action: 'read', subject: 'AffiliatePayout', exact: true },
                    { to: '/affiliates/fraud', i18nKey: 'nav.affiliatesFraud', action: 'read', subject: 'Affiliate', exact: true },
                    { to: '/affiliates/commission-config', i18nKey: 'nav.affiliatesCommissionConfig', action: 'read', subject: 'Affiliate', exact: true },
                    { to: '/affiliates/support', i18nKey: 'nav.affiliatesSupport', action: 'read', subject: 'Ticket', exact: true },
                ],
            },
        ],
    },
    {
        label: 'AGENT FORCE',
        i18nKey: 'nav.groups.agentForce',
        items: [
            {
                to: '/agent-force/agents',
                i18nKey: 'nav.agentForce',
                icon: Network,
                action: 'read',
                subject: 'AgentForce',
                children: [
                    { to: '/agent-force/agents', i18nKey: 'nav.agentForceAgents', action: 'read', subject: 'AgentForce', exact: true },
                    { to: '/agent-force/os', i18nKey: 'nav.agentForceOs', action: 'read', subject: 'AgentForce', exact: true },
                ],
            },
        ],
    },
    {
        label: 'PLATFORM INTELLIGENCE',
        i18nKey: 'nav.groups.intelligence',
        items: [
            { to: '/dashboard/reports', i18nKey: 'nav.reports', icon: FileBarChart, action: 'read', subject: 'Report' },
            { to: '/dashboard/intelligence', i18nKey: 'nav.intelligenceControl', icon: Bot, action: 'read', subject: 'AgentWorkflow' },
            { to: '/dashboard/system', i18nKey: 'nav.systemHealth', icon: Cpu, action: 'read', subject: 'SystemHealth' },
            { to: '/dashboard/deploys', i18nKey: 'nav.deploys', icon: Workflow, action: 'read', subject: 'Deployment' },
            { to: '/dashboard/mcp-catalog', i18nKey: 'nav.mcpCatalog', icon: BookText, action: 'read', subject: 'MCPCatalog' },
            { to: '/audit-log', i18nKey: 'nav.auditLog', icon: ScrollText, action: 'read', subject: 'AuditLog' },
            { to: '/dashboard/devices', i18nKey: 'nav.devices', icon: Cpu, action: 'read', subject: 'Device' },
            { to: '/dashboard/cancellations', i18nKey: 'nav.cancellations', icon: UserMinus, action: 'read', subject: 'Cancellation' },
            { to: '/dashboard/on-call', i18nKey: 'nav.onCall', icon: BellRing, action: 'read', subject: 'OnCall' },
        ],
    },
    {
        label: 'CONFIGURATION',
        i18nKey: 'nav.groups.configuration',
        items: [
            { to: '/settings/platform-config', i18nKey: 'nav.platformConfig', icon: Settings2, action: 'read', subject: 'PlatformSettings' },
            { to: '/settings/api-keys', i18nKey: 'nav.settingsApiKeys', icon: Fingerprint, action: 'manage', subject: 'ApiKey' },
            { to: '/settings/legal-documents', i18nKey: 'nav.legalDocuments', icon: FileText, action: 'read', subject: 'Compliance' },
            { to: '/settings/feature-flags', i18nKey: 'nav.featureFlags', icon: ToggleRight, action: 'read', subject: 'FeatureFlag' },
            { to: '/settings/webhooks', i18nKey: 'nav.webhooks', icon: Webhook, action: 'read', subject: 'Webhook' },
            { to: '/settings/integrations', i18nKey: 'nav.integrations', icon: Plug, action: 'read', subject: 'Integration' },
            { to: '/settings/workflows', i18nKey: 'nav.workflows', icon: Workflow, action: 'read', subject: 'AgentWorkflow' },
            { to: '/settings/abuse', i18nKey: 'nav.abuse', icon: ShieldAlert, action: 'read', subject: 'AbuseSignal' },
            { to: '/settings/content', i18nKey: 'nav.content', icon: BookText, action: 'read', subject: 'ContentBlock' },
        ],
    },
];
